import { api, mutate } from '../api'
import { bottomSheet } from '../due-bar'
import { lineDrawing } from '../picture-tiles'
import { openMessagesFor } from './rebook'
import { clear, el, shortDate, toast } from '../ui'
import { getMe } from '../me'
import { firstName } from '../late-message'

// More > Old customers (Liam, 26 Oct 2026: "i need a tab i can access that cleary shows me when last replied aor worked
// and bin icons so i can manually reviews and do it"; designs https://claude.ai/artifact/XBBEu53mwPMSYAxAEvWbez).
// Only the people Liam put here himself (27 Oct 2026, design B of https://claude.ai/artifact/1YEVfgqUzyxqKK61sTeCir):
// Taken off, Lapsed and Enquiries as three picture tiles, longest ago first, with when they were last cleaned, when they last
// replied, "Over 1 year" or "Over 2 years" as a label, Ask back on a lapsed row Messages can write to, and a bin. The bin wipes how to reach them and what was written about them; their visits
// and money stay for the tax records (functions/api/customers/[id]/bin.ts). Its Undo lasts a minute: the bin is only
// sent once the minute is up, so Undo never has anything to put back.

interface OldRow {
  id: number; name: string; address: string | null; postcode: string | null; version: number
  why?: string; let_go_on?: string | null
  tab: Tab; taken_off: boolean; taken_off_on: string | null; lapsed: boolean; ask_back: boolean; last_in_at: string | null
  last_done: string | null; last_replied: string | null
  over_1_year: boolean; over_2_years: boolean
  next_booked: string | null; owed_pence: number; brought_owes: boolean; binned_at: string | null; bin_refused: string | null
}

type Tab = 'taken_off' | 'lapsed' | 'enquiries'
const TABS: { key: Tab; label: string; drawing: 'personOff' | 'hourglass' | 'bubble'; empty: string; how: string }[] = [
  { key: 'taken_off', label: 'Taken off', drawing: 'personOff', empty: 'Nobody taken off yet',
    how: 'When you send someone the final message, or take them off the round, they show here.' },
  { key: 'lapsed', label: 'Lapsed', drawing: 'hourglass', empty: 'Nobody lapsed yet',
    how: 'When someone stops replying, Messages offers Mark as lapsed. Tap it and they show here.' },
]
TABS.push({ key: 'enquiries', label: 'Enquiries', drawing: 'bubble', empty: 'No enquiries let go yet',
  how: 'Enquiries you let go wait here. Keep them, or bin their details.' })
const BIN_WAIT_MS = 60_000

let rows: OldRow[] = []
let tab: Tab = 'taken_off'
let enquiriesFailed = false
let state: 'loading' | 'ready' | 'failed' = 'loading'
// Bins waiting out their minute, by entity and id: the timer that sends it, and when it goes.
const waiting = new Map<string, { timer: ReturnType<typeof setTimeout>; at: number }>()
let tick: ReturnType<typeof setInterval> | null = null
// A round switched while a bin waits: its countdown is that round's (storeKey), so this phone's timers start afresh.
let armedFor: number | null | undefined

export function mountOldCustomers(): void {
  const root = document.querySelector<HTMLElement>('#screen')!
  const page = el('div', { class: 'old-page' })
  page.append(el('div', { class: 'page-heading' }, el('div', {}, el('h2', {}, 'Old customers'),
    el('p', { class: 'muted' }, "People taken off, lapsed customers and enquiries let go. Review them, or bin their details."))))
  page.append(el('div', { class: 'old-tiles', role: 'group', 'aria-label': 'Which old customers' }))
  page.append(el('p', { class: 'old-enquiry-note muted', hidden: '' }))
  page.append(el('section', { class: 'old-list', 'aria-live': 'polite' }))
  page.append(el('p', { class: 'old-foot muted' }))
  root.append(page)
  const round = getMe()?.businessId ?? null
  if (armedFor !== undefined && armedFor !== round) { for (const w of waiting.values()) clearTimeout(w.timer); waiting.clear(); rows = [] }
  armedFor = round
  state = rows.length ? 'ready' : 'loading'
  resumeBins()
  draw()
  void load()
}

export function oldCustomersOnDataChanged(): void {
  if (document.querySelector('.old-page')) void load()
}

async function load(): Promise<void> {
  try {
    const [r, enquiries] = await Promise.all([api<{ list: OldRow[] }>('/api/customers/old'),
      api<{ list: Pick<OldRow, 'id' | 'name' | 'address' | 'postcode' | 'version' | 'why' | 'let_go_on' | 'last_in_at' | 'binned_at' | 'bin_refused'>[] }>('/api/leads/old').catch(() => null)])
    enquiriesFailed = enquiries === null
    rows = [...r.list, ...(enquiries?.list ?? []).map(l => ({ ...l, tab: 'enquiries' as const, taken_off: false, taken_off_on: null,
      lapsed: false, ask_back: false, last_done: null, last_replied: l.last_in_at?.slice(0, 10) ?? null,
      over_1_year: false, over_2_years: false, next_booked: null, owed_pence: 0, brought_owes: false }))]
    state = 'ready'
    // Open on the first tab with anyone in it.
    if (!rows.some((r) => r.tab === tab)) tab = TABS.find((t) => rows.some((r) => r.tab === t.key))?.key ?? 'taken_off'
  } catch {
    if (!rows.length) state = 'failed'
  }
  draw()
}

function draw(): void {
  const tiles = document.querySelector<HTMLElement>('.old-tiles')
  const list = document.querySelector<HTMLElement>('.old-list')
  const foot = document.querySelector<HTMLElement>('.old-foot')
  if (!tiles || !list || !foot) return
  const note = document.querySelector<HTMLElement>('.old-enquiry-note')
  if (note) { note.hidden = !enquiriesFailed; note.textContent = enquiriesFailed ? 'Enquiries could not load. Check your signal and come back.' : '' }
  clear(tiles)
  for (const t of TABS) {
    const count = rows.filter((r) => r.tab === t.key && !r.binned_at).length
    const b = el('button', { type: 'button', class: `old-tile${tab === t.key ? ' on' : ''}`, 'aria-pressed': String(tab === t.key) },
      el('span', { class: 'old-tile-ring' }, lineDrawing(t.drawing, 22)),
      el('span', { class: 'old-tile-words' }, el('strong', {}, t.label),
        el('span', {}, state !== 'ready' ? ' ' : count ? `${count} ${count === 1 ? 'person' : 'people'}` : 'Nobody yet')))
    b.addEventListener('click', () => { tab = t.key; draw() })
    tiles.append(b)
  }
  clear(list)
  list.classList.remove('old-list-empty')
  foot.textContent = tab === 'enquiries' ? 'Longest ago first. Enquiries you let go stay here until you choose to bin their details.' : tab === 'lapsed' ? 'Longest ago first. Lapsed people are in Messages too, under Lapsed.'
    : 'Longest ago first. Tap a name for their page. Someone who gets in touch again goes back on the round from their page.'
  if (state === 'loading') { list.append(el('p', { class: 'old-empty muted' }, 'Loading…')); return }
  if (state === 'failed') { list.append(el('p', { class: 'old-empty muted' }, 'Could not load old customers. Check your signal and come back.')); return }
  if (tab === 'enquiries' && enquiriesFailed) { foot.textContent = ''; return }
  const shown = rows.filter((r) => r.tab === tab)
  const live = shown.filter((r) => !r.binned_at)
  const gone = shown.filter((r) => r.binned_at)
  if (!shown.length) {
    // An empty tab: the blob, the one place he waves inside the app (docs/brand/README.md, empty pages).
    const t = TABS.find((x) => x.key === tab)!
    list.classList.add('old-list-empty')
    list.append(el('img', { class: 'old-empty-blob', src: '/mascot-blob-waving.png', alt: '', width: '120', height: '120' }),
      el('h3', {}, t.empty), el('p', { class: 'muted' }, t.how))
    foot.textContent = ''
    return
  }
  for (const r of [...live, ...gone]) list.append(rowFor(r))
}

const monthYear = (iso: string | null): string => {
  if (!iso) return ''
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`)
  return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
}

function rowFor(r: OldRow): HTMLElement {
  if (r.binned_at) {
    return el('div', { class: 'old-row old-row-binned' }, el('div', {},
      el('span', { class: 'old-name' }, 'Details binned'),
      el('div', { class: 'old-sub' }, `Was "${r.name}" · binned ${shortDate(r.binned_at.slice(0, 10))}${r.tab === 'enquiries' ? '' : ' · money records kept'}`)))
  }
  const where = [r.address, r.postcode].filter(Boolean).join(', ')
  const why = r.tab === 'enquiries' ? `Let go${r.let_go_on ? ` ${shortDate(r.let_go_on.slice(0, 10))}` : ''} · ${r.why}` : r.tab === 'taken_off' ? `taken off${r.taken_off_on ? ` ${shortDate(r.taken_off_on)}` : ''}` : 'lapsed'
  const gone = r.over_2_years ? 'Over 2 years' : r.over_1_year ? 'Over 1 year' : ''
  const left = el('div', { class: 'old-who' },
    el('div', { class: 'old-name-line' }, r.tab === 'enquiries' ? el('span', { class: 'old-name' }, r.name) : el('a', { href: `#/customer/${r.id}`, class: 'old-name' }, r.name),
      ...(gone ? [el('span', { class: 'old-gone' }, gone)] : [])),
    el('div', { class: 'old-sub' }, [where, why].filter(Boolean).join(' · ')),
    el('div', { class: 'old-facts' }, ...(r.tab === 'enquiries' ? [] : ['Cleaned ', el('strong', {}, r.last_done ? monthYear(r.last_done) : 'never'),
      ' · ']), 'Replied ', el('strong', {}, r.last_replied ? monthYear(r.last_replied) : 'never')))
  const row = el('div', { class: 'old-row' }, left)
  if (sending.has(rowKey(r))) {
    row.classList.add('old-row-pending')
    row.append(el('div', { class: 'old-pending' }, el('span', {}, 'Binning…')))
    return row
  }
  const pending = waiting.get(rowKey(r))
  if (pending) {
    const undo = el('button', { type: 'button', class: 'old-undo' }, 'Undo')
    undo.addEventListener('click', () => undoBin(rowKey(r)))
    row.classList.add('old-row-pending')
    row.append(el('div', { class: 'old-pending' }, el('span', { class: 'old-pending-words', 'data-at': String(pending.at) }, pendingWords(pending.at)), undo))
    return row
  }
  // Ask back: their win-back card in Messages' Lapsed section, where the message is written and sent.
  const ask = r.ask_back ? el('button', { type: 'button', class: 'btn-secondary old-ask' }, lineDrawing('bubble', 18), 'Ask back') : null
  ask?.addEventListener('click', () => openMessagesFor({ id: r.id, name: r.name }, 'customer'))
  let end: HTMLElement
  if (r.bin_refused) {
    // Never binned while they owe money or have a visit booked: says why instead of a bin.
    end = el('span', { class: 'old-why muted' }, r.owed_pence > 0 || r.brought_owes ? 'Owes money' : r.next_booked ? `Visit ${shortDate(r.next_booked)}` : '')
    row.title = r.bin_refused
  } else {
    end = el('button', { type: 'button', class: 'old-bin', 'aria-label': `Bin ${r.name}'s details` }, lineDrawing('bin', 20))
    end.addEventListener('click', () => openBin(r))
  }
  if (ask) { row.classList.add('old-row-ask'); row.append(el('div', { class: 'old-acts' }, ask, end)) } else row.append(end)
  return row
}

// The name the bin keeps on their visits: the first word and the last word's first letter ("Sam Example" → "Sam E."),
// as the server writes it (functions/lib/old-customers.ts binnedName).
function keptName(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  return words.length <= 1 ? words[0] ?? 'Customer' : `${words[0]} ${words[words.length - 1]!.charAt(0).toUpperCase()}.`
}

const pendingWords = (at: number): string => `Binning in ${Math.max(1, Math.ceil((at - Date.now()) / 1000))}s`

function openBin(r: OldRow): void {
  const who = firstName(r.name) || r.name
  const sheet = bottomSheet(`Bin ${who}'s details`, () => {})
  sheet.dialog.classList.add('old-bin-sheet')
  sheet.head.append(el('h3', {}, `Bin ${who}'s details?`))
  const wiped = el('div', { class: 'old-bin-line old-bin-wiped' }, lineDrawing('bin', 20),
    el('span', {}, el('strong', {}, 'Wiped: '), `their phone, email, address, notes, messages${r.tab === 'enquiries' ? ', quotes' : ''} and photos.${r.tab === 'enquiries' ? '' : ' They leave the round.'}`))
  const kept = el('div', { class: 'old-bin-line old-bin-kept' }, lineDrawing('tick', 20),
    el('span', {}, el('strong', {}, 'Kept: '), r.tab === 'enquiries' ? `their first name and surname initial as "${keptName(r.name)}", dates and why they were let go. A spam number stays blocked.` : `their visits, money and invoices as "${keptName(r.name)}", for your tax records.`))
  const yes = el('button', { type: 'button', class: 'old-bin-yes' }, `Bin ${who}'s details`)
  const no = el('button', { type: 'button', class: 'btn-secondary old-bin-no' }, `Keep ${who}`)
  no.addEventListener('click', () => sheet.close())
  yes.addEventListener('click', () => { sheet.close(); startBin(r) })
  sheet.body.append(wiped, kept, el('p', { class: 'muted old-bin-after' }, r.tab === 'enquiries' ? `Undo is there for a minute after. A new message in that minute keeps their details. After that, the details cannot be brought back.` : `Undo is there for a minute after. Once it's gone, if ${who} gets in touch they start as a new customer.`), yes, no)
}

// The minute's wait, then the bin itself. Kept on this phone for its round (Sonnet and GPT-6.1, 26 Oct 2026), so leaving
// the page, closing Roundly or reloading keeps the countdown: on the way back it carries on, or, its minute up, the bin
// goes then. Once it is sending, Undo has gone.
const rowKey = (r: OldRow): string => `${r.tab === 'enquiries' ? 'lead' : 'customer'}:${r.id}`
const pendingKey = (p: Pending): string => `${p.kind ?? 'customer'}:${p.id}`
interface Pending { kind?: 'lead' | 'customer'; id: number; name: string; version: number; at: number; round: number | null; last_in?: string | null }
const storeKey = (): string => `roundly:pending-bins:${getMe()?.businessId ?? 'none'}`
function stored(): Pending[] {
  try { const v = JSON.parse(localStorage.getItem(storeKey()) ?? '[]'); return Array.isArray(v) ? v : [] } catch { return [] }
}
function store(list: Pending[]): void {
  try { if (list.length) localStorage.setItem(storeKey(), JSON.stringify(list)); else localStorage.removeItem(storeKey()) } catch { /* this phone keeps nothing */ }
}
const sending = new Set<string>()

function arm(p: Pending): void {
  if (waiting.has(pendingKey(p)) || sending.has(pendingKey(p))) return
  const timer = setTimeout(() => fire(p), Math.max(0, p.at - Date.now()))
  waiting.set(pendingKey(p), { timer, at: p.at })
  if (!tick) tick = setInterval(() => {
    if (!waiting.size) { clearInterval(tick!); tick = null; return }
    for (const n of document.querySelectorAll<HTMLElement>('.old-pending-words')) n.textContent = pendingWords(Number(n.dataset.at))
  }, 1000)
}

// Picks up bins left waiting on this phone (mountOldCustomers).
function resumeBins(): void {
  for (const p of stored()) arm(p)
}

function fire(p: Pending): void {
  waiting.delete(pendingKey(p))
  // Another round is open now: this one's bin waits, kept on the phone, for its own round to be opened again.
  if ((getMe()?.businessId ?? null) !== p.round) return
  sending.add(pendingKey(p))
  store(stored().filter((x) => pendingKey(x) !== pendingKey(p)))
  draw()
  void sendBin(p)
}

function startBin(r: OldRow): void {
  const p: Pending = { kind: r.tab === 'enquiries' ? 'lead' : 'customer', id: r.id, name: r.name, version: r.version, at: Date.now() + BIN_WAIT_MS, round: getMe()?.businessId ?? null, last_in: r.last_in_at }
  store([...stored().filter((x) => pendingKey(x) !== rowKey(r)), p])
  arm(p)
  draw()
}

function undoBin(id: string): void {
  const w = waiting.get(id)
  if (!w) return
  clearTimeout(w.timer)
  waiting.delete(id)
  store(stored().filter((x) => pendingKey(x) !== id))
  draw()
}

async function sendBin(p: Pending): Promise<void> {
  const ack = await mutate({ method: 'POST', path: `/api/${p.kind === 'lead' ? 'leads' : 'customers'}/${p.id}/bin`, label: `Bin details: ${p.name}`, // A bin waiting from before last_in was kept sends none, so it is not checked for replies (an older list never said).
    body: 'last_in' in p ? { version: p.version, last_in: p.last_in ?? null } : { version: p.version } })
  sending.delete(pendingKey(p))
  if (ack.ok) toast(`${p.name}'s details are binned.`)
  else if (ack.status === 0) toast(`No signal: ${p.name}'s details will be binned when it is back.`)
  else toast(ack.error ?? `Could not bin ${p.name}'s details.`)
  void load()
}
